import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, ButtonBase, Switch} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {useNavigate} from 'react-router-dom';
import {FOND_PAGE, HEADER, NAVY, TEXTE, TYPO} from '../tokens.js';
import {rechercherPav} from '../selectors/recherche.js';
import {useCollecte} from '../collecte.js';

const ONGLETS = [
    {id: 'dashboard', libelle: 'Dashboard', chemin: (slug) => `/pilotage/${slug}`},
    {id: 'carte', libelle: 'Carte', chemin: (slug) => `/pilotage/${slug}/carte`},
];

function Recherche({slug, index}) {
    const navigate = useNavigate();
    const [saisie, setSaisie] = useState('');
    const [ouvert, setOuvert] = useState(false);
    const [actif, setActif] = useState(0);
    const conteneur = useRef(null);
    const suggestions = useMemo(() => rechercherPav(index, saisie, 8), [index, saisie]);

    useEffect(() => {
        const fermer = (e) => { if (conteneur.current && !conteneur.current.contains(e.target)) setOuvert(false); };
        document.addEventListener('mousedown', fermer);
        return () => document.removeEventListener('mousedown', fermer);
    }, []);

    const aller = (pav) => {
        setOuvert(false);
        setSaisie('');
        navigate(`/pilotage/${slug}/pav/${pav.id}`);
    };
    const touche = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActif((a) => Math.max(0, Math.min(a + 1, suggestions.length - 1))); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActif((a) => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter' && suggestions[actif]) { e.preventDefault(); aller(suggestions[actif]); }
        else if (e.key === 'Escape') setOuvert(false);
    };
    const liste = ouvert && saisie.trim().length >= 2;

    return (
        <Box ref={conteneur} sx={{position: 'relative', flex: '0 0 auto'}}>
            <Box component="label" sx={{display: 'flex', alignItems: 'center', gap: '8px', height: 40, px: '14px', backgroundColor: '#FFFFFF', borderRadius: '999px', width: HEADER.rechercheLargeur, boxSizing: 'border-box'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A929C" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
                <Box component="span" sx={{position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)'}}>Rechercher un PAV</Box>
                <Box
                    component="input"
                    type="search"
                    value={saisie}
                    placeholder="Nomenclature, adresse, commune…"
                    role="combobox"
                    aria-expanded={liste}
                    aria-controls="dash-recherche-liste"
                    onChange={(e) => { setSaisie(e.target.value); setActif(0); setOuvert(true); }}
                    onFocus={() => setOuvert(true)}
                    onKeyDown={touche}
                    sx={{border: 0, outline: 0, font: 'inherit', fontSize: 14, color: TEXTE, width: '100%', background: 'transparent'}}
                />
            </Box>
            {liste && (
                <Box
                    id="dash-recherche-liste"
                    role="listbox"
                    sx={{position: 'absolute', top: 46, right: 0, width: '100%', zIndex: 20, backgroundColor: '#FFFFFF', color: TEXTE, borderRadius: '16px', border: '1px solid #E3E7EC', boxShadow: '0 8px 24px rgba(16,46,105,.18)', overflow: 'hidden', whiteSpace: 'normal'}}
                >
                    {suggestions.length === 0 && <Box sx={{p: '12px 16px', fontSize: 13, color: '#6B7480'}}>Aucun PAV trouvé</Box>}
                    {suggestions.map((pav, i) => (
                        <Box
                            key={pav.id}
                            role="option"
                            aria-selected={i === actif}
                            onMouseDown={(e) => { e.preventDefault(); aller(pav); }}
                            onMouseEnter={() => setActif(i)}
                            sx={{p: '8px 16px', cursor: 'pointer', backgroundColor: i === actif ? '#F3F6EC' : 'transparent', borderBottom: i < suggestions.length - 1 ? '1px solid #EEF1F4' : 'none'}}
                        >
                            <Box component="div" sx={{fontSize: 13, fontWeight: 700, color: NAVY}}>
                                {pav.nomenclature}{pav.statut === 'RETIRÉ' ? ' · retiré' : ''}
                            </Box>
                            <Box component="div" sx={{fontSize: 12, color: '#6B7480'}}>
                                {[pav.adresse && String(pav.adresse).trim(), pav.communeLibelle].filter(Boolean).join(' · ') || '—'}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

/**
 * Interrupteur « Collecte » (lot 8) : affiche ou non la collecte simulée sur les trois écrans. Off par défaut.
 * `collecte` / `onCollecte` : surcharge de la page (la carte l'écrit aussi dans son permalien) ; sinon état partagé (localStorage).
 */
function BoutonCollecte({collecte, onCollecte}) {
    const [memorise, memoriser] = useCollecte();
    const actif = collecte === undefined ? memorise : collecte;
    const changer = (e) => (onCollecte || memoriser)(e.target.checked);
    return (
        <Box
            component="label"
            title="Afficher la collecte simulée"
            sx={{display: 'inline-flex', alignItems: 'center', gap: '4px', height: 36, pl: '12px', pr: '6px', mr: '8px', borderRadius: '999px', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#FFFFFF', backgroundColor: actif ? HEADER.ongletActif : 'transparent', border: '1px solid rgba(255,255,255,.28)'}}
        >
            <LocalShippingIcon sx={{fontSize: 18}}/>
            Collecte
            <Switch
                size="small"
                checked={!!actif}
                onChange={changer}
                inputProps={{'aria-label': 'Collecte'}}
                sx={{
                    '& .MuiSwitch-switchBase': {color: '#FFFFFF'},
                    '& .MuiSwitch-switchBase.Mui-checked': {color: '#87BC26'},
                    '& .MuiSwitch-track': {backgroundColor: '#FFFFFF', opacity: 0.45},
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {backgroundColor: '#87BC26', opacity: 0.7},
                }}
            />
        </Box>
    );
}

/**
 * Coquille commune aux trois écrans : header 60 px navy, onglets Dashboard / Carte (+ « Fiche PAV · <nomenclature> »
 * quand `ongletFiche` est fourni), recherche active (nomenclature / adresse / commune, via `index`).
 * `pleinEcran` : main sans padding, hauteur calc(100vh - 60px), sans débordement (Carte).
 * `sansMarge` : main sans padding (l'écran porte sa propre mise en page, Dashboard).
 */
export default function Coquille({slug, onglet, ongletFiche, index, pleinEcran, sansMarge, collecte, onCollecte, children}) {
    const navigate = useNavigate();
    const mainSx = pleinEcran
        ? {p: 0, height: `calc(100vh - ${HEADER.hauteur}px)`, overflow: 'hidden'}
        : {p: sansMarge ? 0 : '24px'};
    return (
        <Box sx={{minHeight: '100vh', background: FOND_PAGE, fontFamily: TYPO.famille, fontSize: 14, lineHeight: 1.45, color: TEXTE}}>
            <Box component="header" sx={{height: HEADER.hauteur, backgroundColor: HEADER.fond, display: 'flex', alignItems: 'center', gap: '8px', px: '24px', color: '#FFFFFF', boxSizing: 'border-box'}}>
                <Box component="span" sx={{fontWeight: 800, fontSize: 16, mr: '16px'}}>Pilotage</Box>
                {ONGLETS.map((o) => (
                    <ButtonBase
                        key={o.id}
                        onClick={() => navigate(o.chemin(slug))}
                        sx={{height: 36, px: '16px', borderRadius: '999px', color: '#FFFFFF', fontWeight: 600, fontSize: 14, backgroundColor: o.id === onglet ? HEADER.ongletActif : 'transparent'}}
                    >
                        {o.libelle}
                    </ButtonBase>
                ))}
                {onglet === 'fiche' && (
                    <ButtonBase
                        aria-current="page"
                        sx={{height: 36, px: '16px', borderRadius: '999px', color: '#FFFFFF', fontWeight: 600, fontSize: 14, backgroundColor: HEADER.ongletActif, cursor: 'default'}}
                    >
                        {`Fiche PAV · ${ongletFiche || '—'}`}
                    </ButtonBase>
                )}
                <Box sx={{flex: 1}}/>
                <BoutonCollecte collecte={collecte} onCollecte={onCollecte}/>
                <Recherche slug={slug} index={index || null}/>
            </Box>
            <Box component={sansMarge ? 'div' : 'main'} sx={mainSx}>{children}</Box>
        </Box>
    );
}
